import React from 'react';
import { useParams } from 'react-router-dom';
import { format, startOfMonth, addMonths } from 'date-fns';
import isEmpty from 'lodash/isEmpty';
import { logWarn } from 'logging';

import PageLoadSpinner from 'components/PageLoadSpinner';
import { RouteModal } from 'catering/components/Modals';
import { useOpenCartNoLongerExistsNotificationModal } from 'catering/components/Modals/NotificationModals';
import { useGoToMenuScreen, useGoToPath } from 'catering/hooks/navigation';
import OrderFulfillmentSettingsForm from 'catering/components/Forms/OrderFulfillmentSettingsForm';
import { useQueryParams } from 'catering/hooks';
import {
  useCart,
  useCateringLocationBySlug,
  useCateringLocationFirstAvailableFulfillmentHours,
  useUpdateCart,
  useCateringLocationFulfillmentHours
} from 'catering/hooks/query';
import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import {
  getAddressStringFromCart,
  getAddressStringFromQueryParams
} from 'helpers/addressHelpers';
import {
  getDateTimeFromFulfillmentDateAndTime,
  getFulfillmentDateKey,
  isCartCouldNotBeFoundError
} from 'catering/helpers';
import { LOCATION_OFFLINE_PATH } from 'catering/constants/paths';

function OrderFulfillmentSettingsContainer() {
  const { locationSlug } = useParams();
  const {
    fulfillmentMethod,
    minFulfillmentDateTime: minDateTimeAsString,
    shipping_city,
    shipping_state,
    shipping_postal_code,
    shipping_google_place_id,
    shipping_street_address_1,
    shipping_country,
    shipping_lat,
    shipping_lng
  } = useQueryParams();

  const minimumFulfillmentDateTime = minDateTimeAsString
    ? new Date(Number(minDateTimeAsString))
    : null;

  const goToMenuScreen = useGoToMenuScreen();
  const { go } = useGoToPath();
  const openCartNoLongerExistsNotificationModal = useOpenCartNoLongerExistsNotificationModal();

  const [initialValues, setInitialValues] = React.useState(null);

  // These state variables correspond to values on the cart that
  // are updated/saved when the user submits the form.
  // They exist as state variables because they don't correspond to inputs in the child form object
  const [deliveryAddress, setDeliveryAddress] = React.useState(null);
  const [fulfillmentMenuId, setFulfillmentMenuId] = React.useState(null);
  const [
    timeWindowIntervalInMinutes,
    setTimeWindowIntervalInMinutes
  ] = React.useState(null);

  const { isLoading: isCartLoading, data: cart } = useCart();
  const {
    isLoading: isLocationLoading,
    data: cateringLocation
  } = useCateringLocationBySlug(locationSlug);

  // if both values are set in query params, it means that the modal is
  // being displayed because the current fulfillment date/time on cart is
  // too soon relative to a product's lead time
  const shouldInitializeForProductLeadTime =
    !!fulfillmentMethod && !!minimumFulfillmentDateTime;

  // Two types of api calls for hours - made conditionally based on value of shouldInitializeForProductLeadTime
  // 1. fetches the first available hour at this location for each fulfillment method
  const {
    isLoading: isFirstAvailableFulfillmentHoursLoading,
    data: firstAvailableFulfillmentHours
  } = useCateringLocationFirstAvailableFulfillmentHours({
    locationSlug,
    customQueryOptions: {
      enabled: !shouldInitializeForProductLeadTime
    }
  });
  // 2. fetches all available hours for this month at this location for a given start date and fulfillment method
  const {
    isLoading: isAllAvailableHoursLoading,
    data: allAvailableHours,
    fetchNextPage: fetchNextFulfillmentHoursPage,
    isFetchingNextPage
  } = useCateringLocationFulfillmentHours({
    locationSlug: locationSlug,
    startDate: minimumFulfillmentDateTime
      ? getFulfillmentDateKey(minimumFulfillmentDateTime)
      : null,
    fulfillmentMethod: fulfillmentMethod,
    customQueryOptions: {
      enabled: shouldInitializeForProductLeadTime
    }
  });

  const [nextPageAlreadyFetched, setNextPageAlreadyFetched] = React.useState(
    false
  );
  const [productLeadTimeAlert, setProductLeadTimeAlert] = React.useState(null);

  const isDataLoading =
    isFirstAvailableFulfillmentHoursLoading ||
    isCartLoading ||
    isLocationLoading ||
    isAllAvailableHoursLoading ||
    isFetchingNextPage;

  // matching menu hour is the first hour where the date/time is greater
  // than the date/time in query params, and the menu_id matches
  // the menu_id that's already set on the cart
  const getFirstValidHourFromHours = hoursObject => {
    const allDatesInObject = Object.keys(hoursObject);

    let validMenuHour = {};
    const firstValidDate = allDatesInObject.find(dateString => {
      const hoursForDate = hoursObject[dateString];
      const validHour = hoursForDate.find(menuHour => {
        const timeString = menuHour.value;
        const menuHourDateTime = getDateTimeFromFulfillmentDateAndTime(
          dateString,
          timeString
        );
        return (
          menuHourDateTime >= minimumFulfillmentDateTime &&
          menuHour.fulfillmentMenuId === cart.fulfillment_menu_id
        );
      });

      if (validHour) {
        validMenuHour = validHour;
      }
      return !!validHour;
    });

    if (!firstValidDate) {
      return null;
    } else {
      return {
        firstValidDate,
        ...validMenuHour
      };
    }
  };

  /**
   * Runs only when modal is opened because product lead time is not satisfied.
   *
   * We can't simply initialize with the the date and time from query params.
   * Those values might not actually match up with a valid menu hour.
   * We need to fetch all available hours and then find the first hour for this menu that
   * is after the date/time in the query params so as to allow for adding of the product.
   */
  const getInitialValuesFromAllAvailableHours = () => {
    if (
      !isAllAvailableHoursLoading &&
      !isFetchingNextPage &&
      allAvailableHours
    ) {
      const firstValidMenuHour = getFirstValidHourFromHours(allAvailableHours);

      if (!firstValidMenuHour && !nextPageAlreadyFetched) {
        const nextMonth = addMonths(minimumFulfillmentDateTime, 1);
        const startOfNextMonth = getFulfillmentDateKey(startOfMonth(nextMonth));

        // Important that we set this, otherwise, we run the risk of requesting
        // an infinite number of requests
        setNextPageAlreadyFetched(true);
        fetchNextFulfillmentHoursPage({ pageParam: startOfNextMonth });
        return {};
      }

      /**
       * This next block fetches 1 more additional month's worth of data
       * This is needed in case the minFulfillmentDateTime is near the end of the month,
       * and hours aren't available until the next month.
       * Ex:
       * - current time is sept 30 9am
       * - additional product lead time of 12 hrs pushes first avail time to sept 30 9pm
       * - menu hours are set from 9am-6pm
       * - the first request for allAvailableHours uses a date of sep 30 to fetch hours
       * - there are no valid hours on sept 30 after 9pm
       * - we then request next month's hours (oct)
       * - we try to find a valid hour in october, otherwise throw an error.
       */
      if (!firstValidMenuHour && nextPageAlreadyFetched) {
        logWarn(
          'Could not find a valid menu hour that satisfies the product lead time'
        );
        return {};
      }

      const {
        firstValidDate,
        fulfillmentMenuId: menuId,
        timeWindowsIntervalInMinutes,
        value: validFulfillmentTime
      } = firstValidMenuHour;

      const validFulfillmentDate = getDateTimeFromFulfillmentDateAndTime(
        firstValidDate,
        validFulfillmentTime
      );

      // The alert message should reflect date/time of the first available menu hour, not
      // necessarily the values from query params for minimum date/time
      const alertMessage = `This item is next available ${format(
        validFulfillmentDate,
        "eee M/d 'at' h:mmaaa"
      )}.`;
      setProductLeadTimeAlert(alertMessage);

      return {
        initialFulfillmentMethod: fulfillmentMethod,
        initialFulfillmentMenu: menuId,
        initialTimeWindowIntervalInMinutes: timeWindowsIntervalInMinutes,
        initialFulfillmentDate: validFulfillmentDate,
        initialFulfillmentTime: validFulfillmentTime
      };
    }
  };

  const transformValuesForForm = ({
    catering_fulfillment_menu_id,
    catering_fulfillment_method,
    catering_fulfillment_time_window_in_minutes,
    fulfillment_date,
    fulfillment_time
  }) => {
    const initialFulfillmentDate = getDateTimeFromFulfillmentDateAndTime(
      fulfillment_date,
      fulfillment_time
    );
    return {
      initialFulfillmentMenu: catering_fulfillment_menu_id,
      initialFulfillmentMethod: catering_fulfillment_method,
      initialTimeWindowIntervalInMinutes: catering_fulfillment_time_window_in_minutes,
      initialFulfillmentTime: fulfillment_time,
      initialFulfillmentDate
    };
  };

  // link to a diagram that illustrates the logic here: https://share.getbento.com/YEuOEq44
  const getInitialValuesFromCartOrFirstAvailable = () => {
    const isFulfillmentMethodSetInQueryParam = !!fulfillmentMethod;
    const isFulfillmentMethodSetOnCart = !!cart.catering_fulfillment_method;
    if (isFulfillmentMethodSetInQueryParam) {
      if (cart?.catering_fulfillment_method !== fulfillmentMethod) {
        // if fulfillment method on cart is different from
        // the fulfillment method the user selected, get new values
        return firstAvailableFulfillmentHours[fulfillmentMethod];
      }
      if (areValuesOnCartValid(cart)) {
        return cart;
      }
      return firstAvailableFulfillmentHours[fulfillmentMethod];
    } else {
      if (!isFulfillmentMethodSetOnCart) {
        // we have no fulfillment method in query params, nor the cart
        // return the first available hour across both methods
        return firstAvailableFulfillmentHours.all;
      }

      if (areValuesOnCartValid(cart)) {
        return cart;
      }

      // check if there are valid hours for the method on the cart
      const hoursForFulfillmentMethodOnCart =
        firstAvailableFulfillmentHours[cart.catering_fulfillment_method];

      // this could happen if this is an old cart and the
      // original fulfillment method is no longer available
      // (e.g, because of cutoff time or lead time)
      if (isEmpty(hoursForFulfillmentMethodOnCart)) {
        return firstAvailableFulfillmentHours.all;
      }
      return hoursForFulfillmentMethodOnCart;
    }
  };

  const getInitialFormValues = () => {
    if (shouldInitializeForProductLeadTime) {
      return getInitialValuesFromAllAvailableHours();
    } else {
      // getInitialValuesFromCartOrFirstAvailable can return values from different
      // sources, but always returns an object with the same snake_cased property
      // names - we call this transform function to combine date + time values,
      // and to convert property names to camelCase
      return transformValuesForForm(getInitialValuesFromCartOrFirstAvailable());
    }
  };

  /**
   * Checks that fulfillment related values are set on the cart.
   * Checks that the fulfillment date & time on the cart are not in the
   * past and not before the first available time at this location
   */
  const areValuesOnCartValid = ({
    catering_fulfillment_menu_id,
    catering_fulfillment_method,
    catering_fulfillment_time_window_in_minutes,
    fulfillment_date,
    fulfillment_time
  }) => {
    if (
      !fulfillment_date ||
      !fulfillment_time ||
      !catering_fulfillment_menu_id ||
      !catering_fulfillment_time_window_in_minutes
    ) {
      return false;
    } else {
      const firstAvailableHour =
        firstAvailableFulfillmentHours[catering_fulfillment_method];

      const fulfillmentDateTimeFromCart = getDateTimeFromFulfillmentDateAndTime(
        fulfillment_date,
        fulfillment_time
      );
      const fulfillmentDateTimeFromFirstAvailableHour = getDateTimeFromFulfillmentDateAndTime(
        firstAvailableHour.fulfillment_date,
        firstAvailableHour.fulfillment_hour
      );

      // TODO - need to handle timezone and localize 'now' relative to location's timezone
      const now = new Date();
      return (
        fulfillmentDateTimeFromCart > now &&
        fulfillmentDateTimeFromCart > fulfillmentDateTimeFromFirstAvailableHour
      );
    }
  };

  /**
   * Logic for determining the initial values that are passed as props to the child form component.
   *
   * Most values are passed into the form directly. If the user makes changes, we are able to
   * get the new values by reading from the form state upon submission.
   *
   * However, there are some values that don't correspond to inputs on the form, and
   * these values are stored as React state variables and updated manually.
   * (fulfillmentMenuId, timeWindowIntervalInMinutes, deliveryAddress)
   *
   * These values can come from 3 sources:
   *  - if modal is opened b/c of product lead time, use query params and allAvailableHours
   *  - otherwise, use the values on the cart if they are already set and still valid
   *  - final fallback is to use the first available hour for this fulfillment method
   *
   * When setting new values, ensure that all related values are updated together.
   * e.g., if we initialize based on the first available menu hour, we set a new menu ID, date and time,
   * but we also need to make sure we update the time window interval as well.
   *
   */
  const hasAddressParams =
    shipping_city &&
    shipping_state &&
    shipping_postal_code &&
    shipping_google_place_id &&
    shipping_street_address_1 &&
    shipping_country &&
    shipping_lat &&
    shipping_lng;

  const getDeliveryAddressForInitialValues = () => {
    if (hasAddressParams) {
      return getAddressStringFromQueryParams(
        shipping_street_address_1,
        shipping_city,
        shipping_state
      );
    }
    return cart &&
      cart.catering_fulfillment_method !== fulfillmentOptions.PICKUP
      ? getAddressStringFromCart(cart)
      : null;
  };
  const setInitialFormValues = async () => {
    if (isDataLoading) {
      return;
    }
    // Redirect to offline page if no firstAvailableFulfillmentHours
    // see PSE-6290 for context
    if (
      !shouldInitializeForProductLeadTime &&
      !firstAvailableFulfillmentHours
    ) {
      logWarn(
        `Attempted to retrieve firstAvailableFulfillmentHours but none are available - ${locationSlug}`
      );
      return go({ path: LOCATION_OFFLINE_PATH, isRedirect: true });
    }
    // delivery address values are initialized with query params if they are passed in
    // otherwise they are based on what is on the cart
    if (hasAddressParams) {
      setDeliveryAddress({
        shipping_street_address_1: shipping_street_address_1,
        shipping_city: shipping_city,
        shipping_state: shipping_state,
        shipping_postal_code: shipping_postal_code,
        shipping_country: shipping_country,
        shipping_google_place_id: shipping_google_place_id,
        shipping_lat: shipping_lat,
        shipping_lng: shipping_lng
      });
    } else {
      setDeliveryAddress({
        shipping_street_address_1: cart?.shipping_street_address_1,
        shipping_city: cart?.shipping_city,
        shipping_state: cart?.shipping_state,
        shipping_postal_code: cart?.shipping_postal_code,
        shipping_country: cart?.shipping_country,
        shipping_google_place_id: cart?.shipping_google_place_id,
        shipping_lat: cart?.shipping_lat,
        shipping_lng: cart?.shipping_lng
      });
    }

    const initialFormValues = getInitialFormValues();

    if (isEmpty(initialFormValues)) {
      if (!nextPageAlreadyFetched) {
        // return early only in case we need to fetch additional data
        return;
      }
      // TODO - determine the ideal ux here - throw an error? show a message that the product isn't available and redirect?
      return goToMenuScreen();
    }

    const {
      initialFulfillmentMethod,
      initialFulfillmentMenu,
      initialTimeWindowIntervalInMinutes,
      initialFulfillmentDate,
      initialFulfillmentTime
    } = initialFormValues;

    setFulfillmentMenuId(initialFulfillmentMenu);
    setTimeWindowIntervalInMinutes(initialTimeWindowIntervalInMinutes);
    setInitialValues({
      catering_fulfillment_method: initialFulfillmentMethod,
      fulfillment_date: initialFulfillmentDate,
      fulfillment_time: initialFulfillmentTime,
      shipping_street_address_2: cart?.shipping_street_address_2 ?? '',
      full_delivery_address: getDeliveryAddressForInitialValues()
    });
  };
  React.useEffect(() => {
    setInitialFormValues();
  }, [
    cateringLocation,
    cart,
    firstAvailableFulfillmentHours,
    allAvailableHours,
    isDataLoading
  ]);

  const updateCart = useUpdateCart();
  const handleSubmit = async formState => {
    let errorMessage = '';
    try {
      await updateCart.mutateAsync({
        catering_fulfillment_method: formState.catering_fulfillment_method, // changed by user when selecting different tab
        catering_location_id: cateringLocation.id,
        shipping_street_address_1: deliveryAddress.shipping_street_address_1,
        shipping_street_address_2: formState.shipping_street_address_2,
        shipping_city: deliveryAddress.shipping_city,
        shipping_state: deliveryAddress.shipping_state,
        shipping_postal_code: deliveryAddress.shipping_postal_code,
        shipping_country: deliveryAddress.shipping_country,
        shipping_google_place_id: deliveryAddress.shipping_google_place_id,
        shipping_lat: deliveryAddress.shipping_lat,
        shipping_lng: deliveryAddress.shipping_lng,
        fulfillment_date: getFulfillmentDateKey(formState.fulfillment_date),
        fulfillment_time: formState.fulfillment_time,
        catering_fulfillment_menu_id: fulfillmentMenuId,
        catering_fulfillment_time_window_in_minutes: timeWindowIntervalInMinutes
      });
    } catch ({ message }) {
      errorMessage = message || '';
    } finally {
      if (isCartCouldNotBeFoundError(errorMessage)) {
        openCartNoLongerExistsNotificationModal();
      } else {
        goToMenuScreen();
      }
    }
  };

  if (isDataLoading) {
    return <PageLoadSpinner />;
  }

  return (
    initialValues && (
      <RouteModal
        onClose={goToMenuScreen}
        productLeadTimeAlert={productLeadTimeAlert}
      >
        <OrderFulfillmentSettingsForm
          cart={cart}
          cateringLocation={cateringLocation}
          deliveryAddress={deliveryAddress}
          firstAvailableFulfillmentHours={firstAvailableFulfillmentHours}
          fulfillmentMenuId={fulfillmentMenuId}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          setDeliveryAddress={setDeliveryAddress}
          setFulfillmentMenuId={setFulfillmentMenuId}
          setTimeWindowIntervalInMinutes={setTimeWindowIntervalInMinutes}
        />
      </RouteModal>
    )
  );
}

export default OrderFulfillmentSettingsContainer;
